/**
 * Mazie Days custom cart drawer.
 *
 * Design notes / why it's built this way:
 * - We do NOT intercept the theme's existing add-to-cart form submissions
 *   directly (the theme's own bundled theme.js already handles those,
 *   controlled by the "Cart" > "When Add to Cart is clicked" setting,
 *   currently "Open popup with checkout button"). Double-handling that
 *   submit would risk adding the item to the cart twice. Instead, like the
 *   existing Klaviyo snippet in layout/theme.liquid, we watch window.fetch
 *   for completed /cart/add.js, /cart/change.js and /cart/update.js calls
 *   and refresh the drawer's contents in the background.
 * - The drawer markup itself lives in sections/cart-drawer.liquid and is
 *   present on every page (included from layout/theme.liquid). We refresh
 *   it using Shopify's section rendering pattern:
 *     fetch(`${routes.cart_url}?section_id=cart-drawer`)
 * - Cross-sell recommendations use Shopify's native Product Recommendations
 *   endpoint with intent=complementary (the same "Pairs well with" data
 *   Search & Discovery drives on the PDP), fetched for whichever product
 *   was most recently in the cart. Shopify's liquid rendering for that
 *   endpoint always points each "Add" button at the recommended product's
 *   own default/first-available variant, with no awareness of what color is
 *   already in the shopper's cart (e.g. a Beige bag would still recommend
 *   the Black Flex Fit Strap). matchRecommendationVariantsToCartColor() is a
 *   client-side pass that runs after each recommendations render to fix
 *   that up:
 *     1. Figures out the color of the most recently added cart line. Most
 *        products on this store (bags, pouches) encode color as a SEPARATE
 *        PRODUCT per color (e.g. "The Mazie - Beige" is its own product
 *        with a single "Default Title" variant) rather than as a variant
 *        option, so there's usually no "Color" option to read — we instead
 *        parse the color word off the end of the product title. A few
 *        products (like Flex Fit Strap) DO use a real "Color" variant
 *        option, so that's checked first when present.
 *     2. For each recommended product, fetches its variant list
 *        (`{product-url}.js`) and swaps the "Add" button's variant id (and
 *        the thumbnail) to whichever variant's options include that color,
 *        if one exists.
 *   If there's no cart color or no matching variant, the original default
 *   variant from Shopify's recommendation is left untouched — this is a
 *   progressive enhancement, not a hard requirement.
 * - We deliberately do NOT auto-open the drawer on every add-to-cart, since
 *   the theme already shows its own small "Added to cart" popup at that
 *   moment (see the Cart setting above), and popping open a second, larger
 *   panel at the same time would be a jarring double-popup. The drawer opens
 *   when the shopper clicks the cart icon, or when they add something from
 *   inside the drawer itself (recommendations, qty change). If you'd rather
 *   retire the small built-in popup and make this drawer the sole
 *   add-to-cart response, change "Cart" > "When Add to Cart is clicked" to
 *   "Stay on the same page" in Theme settings, then flip AUTO_OPEN_ON_ADD
 *   to true below.
 * - Free gift add/remove is now fully owned by the BOGOS app (Apps > BOGOS
 *   in Shopify admin). The theme used to auto-add/remove a real product
 *   variant itself via a "Surprise Gift" native discount, but that discount
 *   has been retired in favor of BOGOS's own auto-add engine, which
 *   correctly evaluates the real cart total (including preorder/
 *   selling-plan line items, which Shopify's native BXGY purchase-amount
 *   check used to exclude). This file no longer adds or removes any gift
 *   line item — sections/cart-drawer.liquid just detects and styles
 *   whatever BOGOS has already put in the cart (matched by its gift
 *   product's handle, which always contains "freegift").
 */
var AUTO_OPEN_ON_ADD = true;
(function () {
  'use strict';

  function getRoutes() {
    return (window.theme && window.theme.routes) || {
      cart_url: '/cart',
      cart_add_url: '/cart/add',
      cart_change_url: '/cart/change'
    };
  }

  var drawerEl = null;
  var isRefreshing = false;
  var pendingRefresh = false;

  function getDrawer() {
    return document.getElementById('cart-drawer');
  }

  function openDrawer() {
    var el = getDrawer();
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  function closeDrawer() {
    var el = getDrawer();
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  function isDrawerOpen() {
    var el = getDrawer();
    return !!(el && el.classList.contains('is-open'));
  }

  function refreshDrawer(thenOpen) {
    if (isRefreshing) {
      pendingRefresh = true;
      return Promise.resolve();
    }
    isRefreshing = true;

    var routes = getRoutes();
    var url = routes.cart_url + (routes.cart_url.indexOf('?') > -1 ? '&' : '?') + 'section_id=cart-drawer';

    return fetch(url)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var freshDrawer = doc.getElementById('cart-drawer');
        var currentDrawer = getDrawer();

        if (freshDrawer && currentDrawer) {
          var wasOpen = currentDrawer.classList.contains('is-open');
          currentDrawer.innerHTML = freshDrawer.innerHTML;
          if (wasOpen || thenOpen) {
            currentDrawer.classList.add('is-open');
            currentDrawer.setAttribute('aria-hidden', 'false');
          }
          updateCartCountBadge();
          fetchRecommendations();
        }
      })
      .catch(function (err) {
        console.error('[cart-drawer] refresh failed', err);
      })
      .finally(function () {
        isRefreshing = false;
        if (pendingRefresh) {
          pendingRefresh = false;
          refreshDrawer();
        }
        // Free gift add/remove is fully owned by the BOGOS app now — no
        // theme-side sync step needed here (see top-of-file note).
      });
  }

  function updateCartCountBadge() {
    // Best-effort: keep the header cart count bubble in sync too.
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var badge = document.querySelector('.cart .cart-icon--basket1 div, .cart .cart-icon--basket2 div');
        if (badge) badge.textContent = cart.item_count;
      })
      .catch(function () {});
  }

  function fetchRecommendations() {
    var container = document.getElementById('cart-drawer-recommendations');
    if (!container) return;
    var productId = container.getAttribute('data-product-id');
    if (!productId || productId === '') {
      container.innerHTML = '';
      return;
    }

    var routes = getRoutes();
    var recUrl = (routes.product_recommendations_url || '/recommendations/products') +
      '?section_id=cart-drawer-recommendations&product_id=' + encodeURIComponent(productId) +
      '&limit=4&intent=complementary';

    fetch(recUrl)
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var fresh = doc.querySelector('.cart-drawer__recommendations, [id="cart-drawer-recommendations"]') || doc.body;
        container.innerHTML = fresh.innerHTML;
        matchRecommendationVariantsToCartColor(container);
      })
      .catch(function (err) {
        console.error('[cart-drawer] recommendations fetch failed', err);
      });
  }

  // Known color words used across this store's product titles/variants, used
  // to pull a color off the end of a product title like "The Mazie - Beige"
  // or "The Good Pup Treat Pouch - Beige CUSTOM". Extend this list if new
  // colorways are added.
  var KNOWN_COLORS = ['Black', 'Beige', 'Brown', 'Sky Blue', 'Olive', 'Pink', 'White', 'Tan', 'Cream', 'Green', 'Blue', 'Grey', 'Gray', 'Navy', 'Red'];

  function extractColorFromTitle(title) {
    if (!title) return null;
    var cleaned = title.replace(/custom/i, '').trim();
    var parts = cleaned.split(' - ');
    var candidate = parts[parts.length - 1].trim();
    var found = KNOWN_COLORS.filter(function (c) {
      return c.toLowerCase() === candidate.toLowerCase();
    })[0];
    return found || null;
  }

  // --- Color-match cross-sell recommendations to what's already in cart ----
  // Shopify's Product Recommendations liquid always points each "Add" button
  // at the recommended product's default/first-available variant (see the
  // top-of-file note). This looks at the color of the most recently added
  // cart line — first via a real "Color" variant option if the item has one,
  // otherwise parsed off the product title, since most colors on this store
  // are separate products rather than variants — then for each recommended
  // product swaps in the variant (and thumbnail) whose option values include
  // that color, if one exists. Silent no-op when there's no color to match
  // or no matching variant, so the default recommendation still works as a
  // fallback.
  function matchRecommendationVariantsToCartColor(container) {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items || !cart.items.length) return;
        var lastItem = cart.items[cart.items.length - 1];
        var color = null;
        (lastItem.options_with_values || []).forEach(function (opt) {
          if (/^colou?r$/i.test(opt.name) && opt.value && opt.value.toLowerCase() !== 'default title') {
            color = opt.value;
          }
        });
        if (!color) {
          color = extractColorFromTitle(lastItem.product_title);
        }
        if (!color) return;

        var items = container.querySelectorAll('.cart-drawer__cs-item');
        items.forEach(function (itemEl) {
          var titleLink = itemEl.querySelector('.cart-drawer__cs-title');
          var btn = itemEl.querySelector('[data-cart-drawer-quick-add]');
          if (!titleLink || !btn) return;
          var productUrl = titleLink.getAttribute('href');
          if (!productUrl) return;
          var jsonUrl = productUrl.split('?')[0].replace(/\/$/, '') + '.js';

          fetch(jsonUrl)
            .then(function (r) { return r.json(); })
            .then(function (product) {
              var match = (product.variants || []).find(function (v) {
                return v.available && (v.options || []).some(function (val) {
                  return typeof val === 'string' && val.toLowerCase() === color.toLowerCase();
                });
              });
              if (!match) return;

              btn.setAttribute('data-variant-id', match.id);
              if (match.featured_image && match.featured_image.src) {
                var img = itemEl.querySelector('.cart-drawer__cs-image img');
                if (img) img.src = match.featured_image.src;
              }
            })
            .catch(function () { /* leave default variant in place */ });
        });
      })
      .catch(function () { /* leave default variants in place */ });
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
  }

  // --- Cart icon interception (capture phase) ------------------------------
  // The theme's own "cc-animate" navigation script also listens for clicks
  // on the cart icon (via its data-cc-animate-click attribute) and will
  // navigate to /cart regardless of preventDefault in a bubble-phase
  // listener. It also occasionally re-renders the header markup (e.g. after
  // an AJAX add-to-cart), which can restore that attribute even after we
  // remove it once on load. To reliably win, we intercept in the CAPTURE
  // phase (which always runs before bubble-phase listeners) matched by the
  // element's id rather than a data-attribute, and stop the event from
  // reaching the theme's own listener entirely.
  document.addEventListener('click', function (e) {
    var cartIcon = e.target.closest('#cart-icon-bttn');
    if (!cartIcon) return;
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
    if (isDrawerOpen()) {
      closeDrawer();
    } else {
      openDrawer();
      refreshDrawer();
    }
  }, true);

  // --- Event delegation ---------------------------------------------------

  document.addEventListener('click', function (e) {
    // Cart icon uses capture-phase interception (below) so this bubble-phase
    // branch is unreachable for it, but left as a harmless fallback.
    var cartIconTrigger = e.target.closest('[data-cart-drawer-toggle]');
    if (cartIconTrigger) {
      e.preventDefault();
      if (isDrawerOpen()) {
        closeDrawer();
      } else {
        openDrawer();
        refreshDrawer();
      }
      return;
    }

    var closeTrigger = e.target.closest('[data-cart-drawer-close]');
    if (closeTrigger) {
      e.preventDefault();
      closeDrawer();
      return;
    }

    var minusBtn = e.target.closest('[data-cart-drawer-qty-minus]');
    var plusBtn = e.target.closest('[data-cart-drawer-qty-plus]');
    var removeBtn = e.target.closest('[data-cart-drawer-remove]');

    if (minusBtn || plusBtn || removeBtn) {
      e.preventDefault();
      var itemEl = (minusBtn || plusBtn || removeBtn).closest('[data-cart-drawer-item]');
      if (!itemEl) return;
      var line = itemEl.getAttribute('data-line');
      var qtyEl = itemEl.querySelector('[data-cart-drawer-qty]');
      var currentQty = qtyEl ? parseInt(qtyEl.textContent, 10) || 0 : 0;
      var newQty = currentQty;

      if (minusBtn) newQty = Math.max(0, currentQty - 1);
      if (plusBtn) newQty = currentQty + 1;
      if (removeBtn) {
        // Removing a BOGOS-managed gift line just removes it like any other
        // line; BOGOS re-evaluates and re-adds it on its own next pass if
        // the cart is still over its threshold, the same as if the shopper
        // had removed it via BOGOS's own widget.
        newQty = 0;
      }

      var routes = getRoutes();
      var changeUrl = (routes.cart_change_url || '/cart/change') + (String(routes.cart_change_url || '').endsWith('.js') ? '' : '.js');

      postJSON(changeUrl, { line: line, quantity: newQty })
        .then(function () { refreshDrawer(true); })
        .catch(function (err) { console.error('[cart-drawer] change failed', err); });
      return;
    }

    var quickAddBtn = e.target.closest('[data-cart-drawer-quick-add]');
    if (quickAddBtn) {
      e.preventDefault();
      var variantId = quickAddBtn.getAttribute('data-variant-id');
      if (!variantId) return;

      quickAddBtn.disabled = true;

      var routes2 = getRoutes();
      var addUrl = (routes2.cart_add_url || '/cart/add') + (String(routes2.cart_add_url || '').endsWith('.js') ? '' : '.js');

      postJSON(addUrl, { id: variantId, quantity: 1 })
        .then(function () {
          openDrawer();
          return refreshDrawer(true);
        })
        .catch(function (err) { console.error('[cart-drawer] quick add failed', err); })
        .finally(function () { quickAddBtn.disabled = false; });
      return;
    }
  });

  // --- Watch fetch for the theme's own add-to-cart / cart mutation calls --

  function handleCartMutationUrl(url) {
    if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
      if (AUTO_OPEN_ON_ADD) {
        openDrawer();
        refreshDrawer(true);
      } else {
        refreshDrawer(false);
      }
    } else if (/\/cart\/(change|update)(\.js)?(\?|$)/.test(url)) {
      refreshDrawer();
    }
  }

  (function (fetchFn) {
    window.fetch = function () {
      var response = fetchFn.apply(this, arguments);
      response.then(function (res) {
        try {
          var url = res.url || (arguments[0] && arguments[0].url) || '';
          handleCartMutationUrl(url);
        } catch (err) {
          /* no-op */
        }
      });
      return response;
    };
  })(window.fetch);

  // --- Watch the theme's native add-to-cart form submit (poll fallback) ----
  // The theme's own add-to-cart form (product-purchase-form) is submitted via
  // jQuery.ajax, which uses XMLHttpRequest rather than fetch — our fetch
  // patch above never sees it. We tried also patching XMLHttpRequest.prototype
  // directly, but at least one third-party script on this storefront (a
  // personalization/analytics tool) patches XHR *after* our script runs and
  // clobbers our wrapper, so that approach isn't reliable here. Instead we
  // listen for the form's submit event (capture phase, so we see it
  // regardless of what other handlers do) and poll /cart.js for the item
  // count to change, then open/refresh the drawer. This has no dependency on
  // which script "wins" the monkey-patch race.
  function isAddToCartControl(el) {
    if (!el) return false;
    var form = el.closest ? el.closest('form') : null;
    if (!form) return false;
    var action = form.getAttribute('action') || '';
    var formLooksRight = /\/cart\/add(\.js)?(\?|$)/.test(action) || form.classList.contains('product-purchase-form');
    if (!formLooksRight) return false;
    var isSubmitControl = (el.tagName === 'BUTTON' && (el.type === 'submit' || !el.type)) ||
      (el.tagName === 'INPUT' && el.type === 'submit') ||
      el.name === 'add';
    return isSubmitControl;
  }

  // We listen on 'click' (capture phase) rather than 'submit' because this
  // theme's add-to-cart button click handler calls preventDefault() and
  // invokes $.ajax() directly — a real 'submit' event never fires on the
  // form, so a submit listener would never see it. Verified live: clicking
  // the button visibly adds to cart but fires zero 'submit' events.
  document.addEventListener('click', function (e) {
    var control = e.target.closest('button, input[type="submit"]');
    if (!isAddToCartControl(control)) return;

    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (before) {
        var beforeCount = before.item_count;
        var attempts = 0;
        var poll = setInterval(function () {
          attempts++;
          fetch('/cart.js')
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (cart.item_count !== beforeCount) {
                clearInterval(poll);
                if (AUTO_OPEN_ON_ADD) {
                  openDrawer();
                  refreshDrawer(true);
                } else {
                  refreshDrawer(false);
                }
              } else if (attempts >= 12) {
                clearInterval(poll);
              }
            })
            .catch(function () { clearInterval(poll); });
        }, 300);
      })
      .catch(function () { /* no-op */ });
  }, true);

  // --- Wire up the header cart icon to open the drawer instead of nav -----

  document.addEventListener('DOMContentLoaded', function () {
    var cartLink = document.getElementById('cart-icon-bttn');
    if (cartLink) {
      cartLink.setAttribute('data-cart-drawer-toggle', '');
      // The theme's own animated-navigation script binds to this attribute
      // and will navigate to /cart regardless of our preventDefault, so it
      // must be removed once the drawer takes over the click.
      cartLink.removeAttribute('data-cc-animate-click');
    }

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isDrawerOpen()) closeDrawer();
    });
  });
})();
